/**
 *  Custom lookup entities extend a single endpoint to multiple client-side entities. This is
 *  necessary to avoid collisions in the redux state as the entity list is manipulated. We've
 *  decided that isolation is only necessary for entities that have a corresponding page
 *  view (e.g., library docs), is part of a dependent filter set, or usage will require
 *  server-side filtering, paging, or sorting.
 */
import applyReducer from '../applyReducer';
// all current entries below need to watch their baseEntity for changes (e.g., jobs). If this isn't
// required (e.g., reportType), customLookupReducerFactory can be used instead
import dependentLookupReducerFactory from '../factories/dependentLookupReducerFactory';

/**
*  Assigned To Filter
*/
const assignedFilterReportReducer = dependentLookupReducerFactory('assignedFilterReport', 'report', {
  filters: {
    status: 1
  },
  paging: null
});
export const assignedFilterReport = applyReducer(assignedFilterReportReducer.reducers, assignedFilterReportReducer.initState);

/**
 *  contractSpec detail
 */
const jscsReducer = dependentLookupReducerFactory('jobSelectContractSpec', 'job', {
  assignedJobsOnly: true,
  filters: {
    closed: '0'
  }
});
export const jobSelectContractSpec = applyReducer(jscsReducer.reducers, jscsReducer.initState);
const jssReducer = dependentLookupReducerFactory('jobSelectSummary', 'job', {
  assignedJobsOnly: true,
  filters: {
    closed: '0'
  },
  paging: null
});
export const jobSelectSummary = applyReducer(jssReducer.reducers, jssReducer.initState);

/**
 *  contractSpec detail
 */
const csfcsReducer = dependentLookupReducerFactory('contractSpecFilterContractSpec', 'job', {
  filters: {
    active: '1'
  },
  paging: null
});
export const contractSpecFilterContractSpec = applyReducer(csfcsReducer.reducers, csfcsReducer.initState);

/**
 *  dashboard filtering. Include closed jobs
 */
const jsdfReducer = dependentLookupReducerFactory('jobSelectDashboard', 'job', {
  assignedJobsOnly: true
});
export const jobSelectDashboard = applyReducer(jsdfReducer.reducers, jsdfReducer.initState);

/**
 *  document detail
 */
const jsdReducer = dependentLookupReducerFactory('jobSelectDocument', 'job', {
  assignedJobsOnly: true,
  filters: {
    closed: '0'
  }
});
export const jobSelectDocument = applyReducer(jsdReducer.reducers, jsdReducer.initState);
const jsudReducer = dependentLookupReducerFactory('jobSelectUploadDocument', 'job', {
  assignedJobsOnly: true,
  filters: {
    closed: '0'
  }
});
export const jobSelectUploadDocument = applyReducer(jsudReducer.reducers, jsudReducer.initState);

/**
 *  editor detail
 */
const rseReducer = dependentLookupReducerFactory('reportSelectEditor', 'report', {
  reportsWithIssues: true,
  paging: null
});
export const reportSelectEditor = applyReducer(rseReducer.reducers, rseReducer.initState);
const rwNDTReducer = dependentLookupReducerFactory('reportWithNDT', 'report', {
  reportsWithIssues: true,
  paging: null
});
export const reportWithNDT = applyReducer(rwNDTReducer.reducers, rwNDTReducer.initState);
const rwmReducer = dependentLookupReducerFactory('reportWithMaintenance', 'report', {
  reportsWithIssues: true,
  paging: null
});
export const reportWithMaintenance = applyReducer(rwmReducer.reducers, rwmReducer.initState);

/**
 *  asset filters
 */
const jfassetReducer = dependentLookupReducerFactory('jobFilterAsset', 'job', {
  assignedJobsOnly: true,
  searchJobNumberOnly: true,
  filters: {
    closed: '0'
  },
  paging: {
    current: 0,
    size: 20
  }
});
export const jobFilterAsset = applyReducer(jfassetReducer.reducers, jfassetReducer.initState);
const oaReducer = dependentLookupReducerFactory('orgFilterAsset', 'organization', {
  hierarchical: true,
  paging: null
});
export const orgFilterAsset = applyReducer(oaReducer.reducers, oaReducer.initState);
const aaReducer = dependentLookupReducerFactory('associatedAsset', 'asset', {
  hierarchical: true,
  paging: null
});
export const associatedAsset = applyReducer(aaReducer.reducers, aaReducer.initState);
const aaoReducer = dependentLookupReducerFactory('associatedAssetOrganization', 'organization', {
  hierarchical: true,
  paging: null
});
export const associatedAssetOrganization = applyReducer(aaoReducer.reducers, aaoReducer.initState);
const rafr = dependentLookupReducerFactory('reportAssignmentFilterReport', 'reportAssignment', {
  paging: null,
  filters: {
    distinct: true
  }
});
export const reportAssignmentFilterReport = applyReducer(rafr.reducers, rafr.initState);
const iafi = dependentLookupReducerFactory('issueAssignmentFilterIssue', 'issueAssignment', {
  paging: null,
  filters: {
    distinct: true
  }
});
export const issueAssignmentFilterIssue = applyReducer(iafi.reducers, iafi.initState);

/**
 *  document filters
 */
const jfdReducer = dependentLookupReducerFactory('jobFilterDocument', 'job', {
  assignedJobsOnly: true,
  searchJobNumberOnly: true,
  filters: {
    closed: '0'
  }
});
export const jobFilterDocument = applyReducer(jfdReducer.reducers, jfdReducer.initState);

/**
 *  report/file cabinet filters
 */
const jpcfrReducer = dependentLookupReducerFactory('jobAndPhaseCodeFilterReport', 'job', {
  assignedJobsOnly: true,
  searchJobNumberOnly: true,
  filters: {
    closed: '0'
  }
});
export const jobAndPhaseCodeFilterReport = applyReducer(jpcfrReducer.reducers, jpcfrReducer.initState);
const jfaReducer = dependentLookupReducerFactory('jobFilterAdd', 'job', {
  assignedJobsOnly: true,
  filters: {
    closed: '0'
  }
});
export const jobFilterAdd = applyReducer(jfaReducer.reducers, jfaReducer.initState);
const jpReducer = dependentLookupReducerFactory('jobPicker', 'job', {
  filters: {
    closed: 0
  },
  paging: {
    current: 0,
    size: 20
  }
});
export const jobPicker = applyReducer(jpReducer.reducers, jpReducer.initState);
const rtfrReducer = dependentLookupReducerFactory('reportTagFilterReport', 'reportTag', {
  paging: null,
  hierarchical: true
});
export const reportTagFilterReport = applyReducer(rtfrReducer.reducers, rtfrReducer.initState);

/**
 *  other
 */
const jbadReducer = dependentLookupReducerFactory('jobBulkActionDocument', 'job', {
  assignedJobsOnly: true,
  filters: {
    closed: '0'
  },
  paging: null
});
export const jobBulkActionDocument = applyReducer(jbadReducer.reducers, jbadReducer.initState);

/**
 *  employeeJobsFilter
 */
const jeReducer = dependentLookupReducerFactory('jobFilterEmployees', 'job', {
  employeeNumberAsName: true,
  filters: {
    closed: 0
  },
  paging: null
});
export const jobFilterEmployees = applyReducer(jeReducer.reducers, jeReducer.initState);

/**
 *  documentCertificationForm
 */
const dcfReducer = dependentLookupReducerFactory('documentCertificationForm', 'document', {
  paging: null
});
export const documentCertificationForm = applyReducer(dcfReducer.reducers, dcfReducer.initState);

/**
 *  jobGeneralEmployee
 */
const jgeReducer = dependentLookupReducerFactory('jobGeneralEmployee', 'employee', {
  paging: null,
  filters: {
    status: '1',
    jobs: []
  },
  sortDirection: 'DESC'
});
export const jobGeneralEmployee = applyReducer(jgeReducer.reducers, jgeReducer.initState);

/**
 *  assetAssociationsJob
 */
const aajReducer = dependentLookupReducerFactory('assetAssociationsJob', 'job', {
  filters: {
    closed: 0
  },
  paging: null
});
export const assetAssociationsJob = applyReducer(aajReducer.reducers, aajReducer.initState);